import React, { useRef, useState } from 'react';
import { Alert, Button, Typography } from '@one-thd/sui-atomic-components';
import PropTypes from 'prop-types';
import {
  ERROR_ALERT_TEXT,
  PENDING,
  SMS_OPT_IN_SUCCESS,
  UPDATE_SUCCESS,
  YES
} from '../constants';
import {
  scrollToBanner,
  formatIntake
} from '../../utils/general';
import { resendText } from '../apis/common';

export const successBanner = 'success-banner';
export const errorBanner = 'error-banner';
export const undoButton = 'undo-button';
export const smsPendingBanner = 'sms-pending-banner';
export const resendButtonLabel = 'resend-button';
export const smsTelemarketingInfoBanner = 'sms-telemarketing-banner';

function BannerProvider({ children }) {
  const [successAlertText, setSuccessAlertText] = React.useState(UPDATE_SUCCESS);
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
  const [inputAttribute, setInputAttribute] = React.useState('');
  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [showInfoAlert, setShowInfoAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = React.useState(ERROR_ALERT_TEXT);
  const [infoAlertText, setInfoAlertText] = React.useState('');
  const [warningAlertText, setWarningAlertText] = React.useState('');
  const [showWarningAlert, setShowWarningAlert] = React.useState('');
  const [selectedUndo, setSelectedUndo] = React.useState([]);
  const [showIsPending, setShowIsPending] = React.useState(false);
  const [pendingText, setPendingText] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [phoneTableData, setPhoneTableData] = React.useState([]);
  const [showSmsTelemarketing, setShowSmsTelemarketing] = React.useState(false);

  const closePendingAlert = () => {
    setShowIsPending(false);
  };

  function openSmsTelemarketingInfoAlert() {
    setShowSmsTelemarketing(true);
  }
  const closeSmsTelemarketingInfoAlert = () => {
    setShowSmsTelemarketing(false);
  };

  function openPendingAlert(text, number, phoneTable) {
    setShowIsPending(false);
    if (text) {
      setPendingText(text || UPDATE_SUCCESS);
      setShowIsPending(true);
    }
    setPhoneNumber(number);
    setPhoneTableData(phoneTable);
  }
  const scrollToAlert = useRef(null);
  React.useEffect(() => {
    if (scrollToAlert.current && (showSuccessAlert || showInfoAlert || showWarningAlert || showSmsTelemarketing)) {
      scrollToBanner(scrollToAlert);
    }
  }, [showSuccessAlert, showInfoAlert, showWarningAlert, showSmsTelemarketing
  ]);

  function closeSuccessAlert(index) {

    const messages = Array.isArray(successAlertText) ? successAlertText : [successAlertText];
    const updatedMessages = messages.filter((item, i) => i !== index);
    if (updatedMessages.length === 0 || index === undefined) {
      setShowSuccessAlert(false);
    } else {
      setSuccessAlertText(updatedMessages.length === 1 ? updatedMessages[0] : updatedMessages);
    }
  }

  function openSuccessAlert(message, intake) {
    if (intake !== null) {
      setInputAttribute(formatIntake(intake));
    }
    setSuccessAlertText(message);
    setShowSuccessAlert(true);
    setShowErrorAlert(false);
  }

  function openResendSuccessAlert(text) {
    setSuccessAlertText(text || SMS_OPT_IN_SUCCESS);
    setShowSuccessAlert(true);
    setShowErrorAlert(false);
  }

  function openErrorAlert(text) {
    setErrorAlertText(text || ERROR_ALERT_TEXT);
    setShowErrorAlert(true);
    setShowSuccessAlert(false);
  }

  function closeErrorAlert() {
    setShowErrorAlert(false);
  }

  function successAlert() {
    const messages = Array.isArray(successAlertText) ? successAlertText : [successAlertText];

    return (showSuccessAlert && (
      <div ref={scrollToAlert}>
        {messages.map((message, index) => (
          <Alert
            key={index}
            aria-label={successBanner}
            status="success"
            size="large"
            onClose={() => closeSuccessAlert(index)}
          >
            <Typography variant="body-base">
              <Typography component="b" weight="bold">
                {inputAttribute}
              </Typography>
              {message}
            </Typography>
          </Alert>
        ))}
      </div>
    ));
  }

  function errorAlert() {
    return (showErrorAlert && (
      <Alert ref={scrollToAlert} aria-label={errorBanner} status="error" size="large">
        <Typography>{errorAlertText}</Typography>
      </Alert>
    ));
  }

  function infoAlert() {
    return (showInfoAlert && (
      <Alert ref={scrollToAlert} status="info" size="large">
        <Typography>{infoAlertText}</Typography>
      </Alert>
    ));
  }

  function openInfoAlert(text) {
    setInfoAlertText(text);
    setShowInfoAlert(true);
    setErrorAlertText(false);
    setShowSuccessAlert(
      false
    );
  }

  function closeInfoAlert(i) {
    setShowInfoAlert(false);
  }

  function warningAlert() {
    return (showWarningAlert && (
      <Alert ref={scrollToAlert} status="warning" size="large">
        <Typography>{warningAlertText}</Typography>
      </Alert>
    ));
  }

  function openWarningAlert(text) {
    setWarningAlertText(text);
    setShowWarningAlert(true);
    setShowInfoAlert(false);
    setErrorAlertText(false);
    setShowSuccessAlert(
      false
    );
  }

  function closeWarningAlert() {
    setShowWarningAlert(false);
  }

  function handlePendingResend() {
    resendText(phoneNumber, phoneTableData, () => {
      setShowIsPending(false);
      closeErrorAlert();
      closeSuccessAlert();
      openResendSuccessAlert();
    }, (err) => {
      setShowErrorAlert(true);
    });
  }
  function smsTelemarketingInfoAlert() {
    return (
      showSmsTelemarketing && (
        <Alert
          ref={scrollToAlert}
          onClose={closeSmsTelemarketingInfoAlert}
          status="info"
          size="large"
          aria-label={smsTelemarketingInfoBanner}
        >
          <Typography>
            An opt out of one preference will be treated as an opt out of all three preferences below.
          </Typography>
        </Alert>
      )
    );
  }
  function resendButton() {
    return (
      <Button
        aria-label={resendButtonLabel}
        style={{ fontSize: 14, textDecoration: 'underline' }}
        variant="text"
        className="rounded-full"
        onClick={() => handlePendingResend()}
      >
        Resend
      </Button>
    );
  }

  function pendingSmsAlert() {
    return (showIsPending && (
      <div className="sui-flex sui-flex-col sui-w-full">
        <Alert aria-label={smsPendingBanner} onClose={closePendingAlert} status="warning" size="large">
          <div className="sui-flex sui-gap-1">
            <Typography>
              {pendingText}
            </Typography>
            <div className="sui-px-5 sui-py-20 xl:sui-py-2 lg:sui-py-3 md:sui-py-4 sm:sui-py-10 xs:sui-py-20">
              <Typography className="sui-flex sui-flex-center" fontSize="16px">
                {resendButton()}
              </Typography>
            </div>
          </div>
        </Alert>
      </div>
    ));
  }

  return (
    <BannerContext.Provider
      value={{
        openSuccessAlert,
        closeSuccessAlert,
        openErrorAlert,
        openInfoAlert,
        openWarningAlert,
        selectedUndo,
        openPendingAlert,
        closeErrorAlert,
        closeInfoAlert,
        closeWarningAlert,
        closePendingAlert,
        openSmsTelemarketingInfoAlert,
        closeSmsTelemarketingInfoAlert,
      }}
    >
      <div>
        {successAlert()}
        {errorAlert()}
        {infoAlert()}
        {warningAlert()}
        {pendingSmsAlert()}
        {smsTelemarketingInfoAlert()}
        {children}
      </div>
    </BannerContext.Provider>
  );
}

const BannerContext = React.createContext(null);

export { BannerContext, BannerProvider };

BannerProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
};
